import { useState, useEffect, useRef } from "react";
import { useTimeFormatter } from "./useTimer";
import { FileWithMetadata } from "../components/globals/File";

export const useAudioRecorder = () => {
    const { start,stop,time,reset,seconds} = useTimeFormatter();
    const [isRecording, setIsRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioRef = useRef(null);

    // Get access to the microphone
    useEffect(() => {
        const getMicrophonePermission = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaRecorderRef.current = new MediaRecorder(stream);

                // When the recording stops, save the audio
                mediaRecorderRef.current.ondataavailable = (event) => {
                    const audioData = event.data;
                    setAudioBlob(audioData);
                    setAudioUrl(URL.createObjectURL(audioData));
                };
            } catch (err) {
                console.error("Error accessing the microphone", err);
            }
        };

        getMicrophonePermission();

        // Cleanup on unmount
        return () => {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const startRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "inactive") {
            mediaRecorderRef.current.start();
            setIsRecording(true);
            start();
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            stop();
        }
    };

    const resetRecording = () => {
        setAudioUrl(null);
        setAudioBlob(null);
        reset();
    };

    const playAudio = () => {
        if (audioRef.current) {
            audioRef.current.play();
        }
    };

    const downloadAudio = () => {
        if (audioBlob) {
            const url = window.URL.createObjectURL(audioBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "recorded_audio.wav";
            link.click();
        }
    };

    // New function to get the audio as a File object
    const getAudioFile = () => {
        if (audioBlob) {
            // Create a new File object with the duration in the metadata
            const audioFileWithMetadata = new FileWithMetadata([audioBlob], "recorded_audio.wav", {
                type: "audio/wav"
            });
            audioFileWithMetadata.duration = seconds;
            return audioFileWithMetadata;
        }
        return null;
    };

    return {
        time,
        isRecording,
        audioUrl,
        startRecording,
        stopRecording,
        resetRecording,
        playAudio,
        downloadAudio,
        getAudioFile,
        audioRef,
    };
};


export const useVoiceIntensity = () => {
  const [intensity, setIntensity] = useState(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    const initAudio = async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
      microphoneRef.current.connect(analyserRef.current);
      analyzeAudio();
    };

    const analyzeAudio = () => {
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const average = dataArrayRef.current.reduce((sum, value) => sum + value, 0) / dataArrayRef.current.length;
      setIntensity(average);
      requestAnimationFrame(analyzeAudio);
    };

    initAudio();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return intensity;
};


